import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Button, ErrorNote, Spinner } from '../../components/ui';
import { Logo } from '../layout/Logo';
import { Markdown } from '../../components/Markdown';
import { useAccount } from '../plans/useAccount';
import { useAuth } from '../auth/AuthProvider';

/** Shown before anything else when the site is behind a password. */
export function SitePasswordGate({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const s = useQuery({ queryKey: ['site-status'], queryFn: () => api<{ passwordRequired: boolean; unlocked: boolean }>('/api/site/status'), retry: 1 });
  const [pw, setPw] = useState('');
  const unlock = useMutation({ mutationFn: () => api('/api/site/unlock', { body: { password: pw } }), onSuccess: () => qc.invalidateQueries() });
  if (s.isLoading) return <div className="center-fill"><Spinner /></div>;
  // Legal pages are always readable, even behind a site password.
  if (!s.data || s.data.unlocked || window.location.pathname.startsWith('/legal')) return <>{children}</>;
  return (
    <div className="auth">
      <form className="auth__card" onSubmit={(e) => { e.preventDefault(); unlock.mutate(); }}>
        <Logo large />
        <h1>Private beta</h1>
        <p className="muted">Enter the site password to continue. After that you can sign in or sign up as usual.</p>
        <label className="field"><span>Site password</span><input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus /></label>
        <ErrorNote error={unlock.error} />
        <Button variant="primary" type="submit" loading={unlock.isPending} disabled={!pw}>Continue</Button>
      </form>
    </div>
  );
}

/** Blocked, waiting for approval, or needs to accept the beta agreement. */
export function AccountGate({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { logout, user, refresh } = useAuth();
  const [code, setCode] = useState('');
  const verify = useMutation({ mutationFn: () => api('/api/auth/verify', { body: { code } }), onSuccess: async () => { await refresh(); await qc.invalidateQueries(); } });
  const resend = useMutation({ mutationFn: () => api('/api/auth/verify/resend', { body: {} }) });
  const acc = useAccount();
  const text = useQuery({ queryKey: ['agreement'], queryFn: () => api<{ text: string; version: string }>('/api/agreement'), enabled: !!acc.data && acc.data.agreement.required && !acc.data.agreement.accepted });
  const [agree, setAgree] = useState(false);
  const accept = useMutation({ mutationFn: () => api('/api/agreement/accept', { body: {} }), onSuccess: () => qc.invalidateQueries() });
  if (acc.isLoading) return <div className="center-fill"><Spinner /></div>;
  const a = acc.data;
  if (!a) return <>{children}</>;
  const screen = (title: string, body: ReactNode) => (
    <div className="auth"><div className="auth__card"><Logo large /><h1>{title}</h1>{body}<Button variant="ghost" onClick={() => void logout()}>Log out</Button></div></div>
  );
  if (user && user.emailVerified === false) {
    return (
      <div className="auth"><form className="auth__card" onSubmit={(e) => { e.preventDefault(); verify.mutate(); }}>
        <Logo large /><h1>Check your email</h1>
        <p className="muted">We sent a 6-digit code to <strong>{user.email}</strong>. Enter it to finish creating your account.</p>
        <label className="field"><span>Code</span><input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="123456" /></label>
        <ErrorNote error={verify.error ?? resend.error} />
        <Button variant="primary" type="submit" loading={verify.isPending} disabled={code.length !== 6}>Confirm email</Button>
        <Button variant="ghost" type="button" loading={resend.isPending} onClick={() => resend.mutate()}>{resend.isSuccess ? 'Sent again. Check spam too.' : 'Send a new code'}</Button>
        <Button variant="ghost" type="button" onClick={() => void logout()}>Log out</Button>
      </form></div>
    );
  }
  if (a.blocked) return screen('Access removed', <p className="muted">This account can't use Lumnos. If you think this is a mistake, contact the Lumnos team.</p>);
  if (!a.approved) return screen('Almost there', <p className="muted">Your account is waiting for approval. You'll be able to use Lumnos as soon as an administrator approves it.</p>);
  if (a.agreement.required && !a.agreement.accepted) {
    return (
      <div className="auth"><div className="auth__card auth__card--wide">
        <Logo large /><h1>Beta agreement</h1>
        <p className="muted">Please read and accept to continue. A copy will be emailed to you.</p>
        <div className="agreement">{text.data ? <Markdown text={text.data.text} /> : 'Loading…'}</div>
        <label className="check"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> <span>I have read and agree to the Beta User Agreement, the <a href="/legal/terms" target="_blank" rel="noopener">Terms of Service</a> and the <a href="/legal/privacy" target="_blank" rel="noopener">Privacy Policy</a></span></label>
        <ErrorNote error={accept.error} />
        <Button variant="primary" disabled={!agree || !text.data} loading={accept.isPending} onClick={() => accept.mutate()}>Accept and continue</Button>
        <Button variant="ghost" onClick={() => void logout()}>Log out</Button>
      </div></div>
    );
  }
  return <>{children}</>;
}
